import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, onSnapshot, query, orderBy, where, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { TRANSACTION_TYPES } from '../utils/stockUtils';
import { fetchOrgSalesHistory, getForecastForProduct } from '../utils/forecastUtils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Link } from 'react-router-dom';

// ── Demand Forecast Panel ─────────────────────────────────────────────────────
function DemandForecastPanel({ products, lowStockProducts, orgId, isMobile }) {
  const [forecasts, setForecasts]         = useState({});
  const [loading, setLoading]             = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Refs hold latest values so the callback never needs them as deps.
  const productsRef       = useRef(products);
  const lowStockRef       = useRef(lowStockProducts);
  const hasFetchedRef     = useRef(false);

  useEffect(() => { productsRef.current = products; },       [products]);
  useEffect(() => { lowStockRef.current = lowStockProducts; }, [lowStockProducts]);

  const runForecasts = useCallback(async (forceFresh = false) => {
    const currentProducts  = productsRef.current;
    const currentLowStock  = lowStockRef.current;
    if (!orgId || currentProducts.length === 0) return;
    setLoading(true);


    try {
      const salesHistory = await fetchOrgSalesHistory(orgId);

      const lowStockIds = new Set(currentLowStock.map(p => p.id));
      const topSellers = Object.entries(salesHistory)
        .filter(([id]) => !lowStockIds.has(id))
        .sort((a, b) => b[1].length - a[1].length)
        .slice(0, 3)
        .map(([id]) => currentProducts.find(p => p.id === id))
        .filter(Boolean);

      const toForecast = [...currentLowStock.slice(0, 5), ...topSellers];
      if (toForecast.length === 0) { setLoading(false); return; }

      const results = await Promise.allSettled(
        toForecast.map(p => getForecastForProduct(p, salesHistory, orgId, forceFresh))
      );

      const map = {};
      toForecast.forEach((p, i) => {
        if (results[i].status === 'fulfilled') map[p.id] = { product: p, forecast: results[i].value };
      });
      setForecasts(map);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Forecast batch error:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]); // ← orgId only; products accessed via ref

  // Auto-run once when orgId is ready and products have loaded.
  useEffect(() => {
    if (!orgId || products.length === 0 || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    runForecasts();
  }, [orgId, products.length, runForecasts]);

  const entries = Object.values(forecasts).sort((a, b) => {
  const da = a.forecast.daysUntilStockout ?? 9999;
  const db = b.forecast.daysUntilStockout ?? 9999;
  return da - db;
});

  // --- rest of the render is identical, copy from the previous version below this line ---

  const getDaysColor = (days) => {
    if (days === null || days === undefined) return '#94a3b8';
    if (days <= 3)  return '#dc2626';
    if (days <= 7)  return '#f97316';
    if (days <= 14) return '#ca8a04';
    return '#16a34a';
  };

  const getDaysBg = (days) => {
    if (days === null || days === undefined) return '#f8fafc';
    if (days <= 3)  return '#fef2f2';
    if (days <= 7)  return '#fff7ed';
    if (days <= 14) return '#fefce8';
    return '#f0fdf4';
  };

  const getDaysLabel = (days) => {
    if (days === null || days === undefined) return 'No data';
    if (days === 999) return 'Not selling';
    if (days <= 0) return 'Stockout now';
    return `~${days} days left`;
  };

  const confidenceDot = (conf) => {
    const colors = { high: '#16a34a', medium: '#ca8a04', low: '#94a3b8' };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: colors[conf] || '#94a3b8' }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: colors[conf] || '#94a3b8', display: 'inline-block', flexShrink: 0 }} />
        {conf} confidence
      </span>
    );
  };

  const hasEntries = entries.length > 0;

  return (
    <div style={fpStyles.panel}>
      {/* Header */}
      <div style={fpStyles.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <h3 style={fpStyles.title}>🤖 Demand Forecast</h3>
            <span style={fpStyles.geminiTag}>Gemini</span>
          </div>
          <p style={fpStyles.subtitle}>
            AI-powered stockout predictions · cached 24h
            {lastRefreshed && !loading && (
              <span style={{ marginLeft: '6px', color: '#94a3b8' }}>
                · last run {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => runForecasts(true)}
          disabled={loading}
          style={loading ? fpStyles.refreshBtnDisabled : fpStyles.refreshBtn}
          title="Force refresh — bypasses 24h cache"
        >
          {loading ? '...' : '↺ Refresh'}
        </button>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div>
          {[1, 2, 3].map(i => (
            <div key={i} style={fpStyles.skeletonRow}>
              <div style={{ ...fpStyles.skeletonBlock, width: '40%' }} />
              <div style={{ ...fpStyles.skeletonBlock, width: '18%' }} />
              <div style={{ ...fpStyles.skeletonBlock, width: '22%' }} />
            </div>
          ))}
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px', textAlign: 'center' }}>
            Asking Gemini for uncached products…
          </p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !hasEntries && (
        <div style={fpStyles.emptyState}>
          <span style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }}>📊</span>
          <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
            Record at least 3 sales transactions to unlock demand forecasting.
          </p>
        </div>
      )}

      {/* Forecast rows */}
      {!loading && entries.map(({ product, forecast }) => {
        const days = forecast.daysUntilStockout;
        const daysColor = getDaysColor(days);
        const daysBg = getDaysBg(days);

      return (
  <div key={product.id} style={fpStyles.row}>
    <div style={fpStyles.rowLeft}>
      <div style={fpStyles.productName}>{product.name}</div>
      <div style={fpStyles.rowMeta}>
        <span style={fpStyles.metaChip}>📦 Stock: {product.stock}</span>
        {forecast.avgDailyConsumption > 0 && (
          <span style={fpStyles.metaChip}>
            📉 avg {Number(forecast.avgDailyConsumption).toFixed(1)} units/day
          </span>
        )}
        {confidenceDot(forecast.confidence)}
        {forecast.fromCache && (
          <span style={fpStyles.cachedBadge}>⚡ cached</span>
        )}
      </div>
      {forecast.reason && (
        <div style={fpStyles.reason}>💬 {forecast.reason}</div>
      )}
    </div>

    <div style={fpStyles.rowRight}>
      <span style={{
        ...fpStyles.daysBadge,
        color: daysColor,
        backgroundColor: daysBg,
        borderColor: daysColor,
      }}>
        {getDaysLabel(days)}
      </span>
      {forecast.recommendedOrderQty && (
        <span style={fpStyles.orderBadge}>
          🛒 Order {forecast.recommendedOrderQty} units
        </span>
      )}
    </div>
  </div>
);
      })}
    </div>
  );
}

const fpStyles = {
  panel: {
    background: '#ffffff',
    borderTop: '1px solid #e2e8f0',
    borderRight: '1px solid #e2e8f0',
    borderBottom: '1px solid #e2e8f0',
    borderLeft: '4px solid #6366f1',
    borderRadius: '14px',
    padding: '24px 26px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    marginBottom: '28px',
  },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '20px', gap: '12px',
  },
  title: { fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 },
  geminiTag: {
    fontSize: '11px', fontWeight: '700', color: '#6366f1',
    backgroundColor: '#eef2ff', padding: '3px 10px',
    borderRadius: '20px', border: '1px solid #c7d2fe',
    letterSpacing: '0.04em',
  },
  subtitle: { fontSize: '13px', color: '#64748b', margin: 0 },
  refreshBtn: {
    padding: '8px 18px', border: '1.5px solid #e2e8f0',
    borderRadius: '8px', backgroundColor: '#ffffff',
    color: '#475569', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', flexShrink: 0,
  },
  refreshBtnDisabled: {
    padding: '8px 18px', border: '1.5px solid #e2e8f0',
    borderRadius: '8px', backgroundColor: '#f8fafc',
    color: '#94a3b8', fontSize: '13px', cursor: 'not-allowed', flexShrink: 0,
  },
  skeletonRow: {
    display: 'flex', gap: '10px', alignItems: 'center',
    padding: '16px 0', borderBottom: '1px solid #f1f5f9',
  },
  skeletonBlock: {
    height: '16px', borderRadius: '6px',
    backgroundColor: '#f1f5f9',
  },
  emptyState: {
    textAlign: 'center', padding: '40px 0', color: '#94a3b8',
  },
  row: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', gap: '16px',
    padding: '16px 0', borderBottom: '1px solid #f1f5f9',
    flexWrap: 'wrap',
  },
  rowLeft: { flex: 1, minWidth: 0 },
  rowRight: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-end', gap: '8px', flexShrink: 0,
  },
  productName: {
    fontSize: '16px', fontWeight: '700', color: '#1e293b',
    marginBottom: '6px', overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  rowMeta: {
    display: 'flex', alignItems: 'center',
    gap: '8px', flexWrap: 'wrap', marginBottom: '4px',
  },
  metaChip: {
    fontSize: '12px', color: '#475569',
    backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
    padding: '3px 10px', borderRadius: '20px', fontWeight: '500',
  },
  cachedBadge: {
    fontSize: '11px', color: '#94a3b8',
    backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
    padding: '2px 8px', borderRadius: '20px',
  },
  reason: {
    fontSize: '12px', color: '#64748b', fontStyle: 'italic', marginTop: '3px',
  },
  daysBadge: {
    fontSize: '14px', fontWeight: '800',
    padding: '6px 16px', borderRadius: '20px',
    border: '2px solid currentColor', whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
  },
  orderBadge: {
    fontSize: '13px', fontWeight: '700',
    color: '#6366f1', backgroundColor: '#eef2ff',
    padding: '5px 14px', borderRadius: '20px',
    border: '1.5px solid #c7d2fe', whiteSpace: 'nowrap',
  },
};
// ─────────────────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, color = '#64748b', bg = '#f8fafc', to }) {
  const inner = (
    <div style={{
      background: '#ffffff',
      borderTop: '1px solid #e2e8f0',
      borderRight: '1px solid #e2e8f0',
      borderBottom: '1px solid #e2e8f0',
      borderLeft: `4px solid ${color}`,
      borderRadius: '14px', padding: '20px 22px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'box-shadow 0.2s, transform 0.2s',
      cursor: to ? 'pointer' : 'default',
    }}
      onMouseEnter={e => { if (to) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>{icon}</div>
        {to && <span style={{ fontSize: '12px', color: '#94a3b8' }}>View →</span>}
      </div>
      <div style={{ fontSize: '30px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '4px' }}>{value}</div>
      <div style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '2px' }}>{label}</div>
      {sub && <div style={{ fontSize: '12px', color: '#94a3b8' }}>{sub}</div>}
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{inner}</Link> : inner;
}

function MiniActivityRow({ log }) {
  const txn = TRANSACTION_TYPES[log.transactionType];
  const delta = log.delta;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: txn?.bg || '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>
        {txn?.label?.[0] || '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.productName}</div>
        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{log.userName} · {log.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
      <span style={{ fontSize: '14px', fontWeight: '800', color: delta > 0 ? '#16a34a' : delta < 0 ? '#ef4444' : '#64748b', flexShrink: 0 }}>
        {delta > 0 ? `+${delta}` : delta}
      </span>
      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: txn?.bg, color: txn?.color, fontWeight: '600', flexShrink: 0 }}>
        {txn?.label}
      </span>
    </div>
  );
}

function LowStockRow({ product }) {
  const deficit = product.minStock != null
    ? product.minStock - product.stock
    : product.moq - product.stock;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{product.group}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '800', color: '#ef4444' }}>{product.stock}</div>
        <div style={{ fontSize: '10px', color: '#94a3b8' }}>need {deficit} more</div>
      </div>
    </div>
  );
}

function InventoryValuePanel({ totalValue, groupBreakdown, topValueProducts, productsWithCost, totalProducts, isMobile }) {
  const [tab, setTab] = useState('groups');
  const maxGroupValue = groupBreakdown[0]?.value || 1;
  const maxProductValue = topValueProducts[0]?.totalValue || 1;

  if (totalValue === 0) {
    return (
      <div style={ivpStyles.emptyBox}>
        <span style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }}>💰</span>
        <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
          Add cost prices to products to see inventory value breakdown
        </p>
      </div>
    );
  }

  return (
    <div style={ivpStyles.panel}>
      <div style={ivpStyles.header}>
        <div>
          <h3 style={ivpStyles.title}>Inventory Value</h3>
          <p style={ivpStyles.subtitle}>
            <span style={ivpStyles.totalValueBig}>₹{totalValue.toLocaleString('en-IN')}</span>
            <span style={ivpStyles.pricedCount}> · {productsWithCost.length} of {totalProducts} products priced</span>
          </p>
        </div>
        <div style={ivpStyles.tabs}>
          <button onClick={() => setTab('groups')} style={tab === 'groups' ? ivpStyles.tabActive : ivpStyles.tabInactive}>By group</button>
          <button onClick={() => setTab('products')} style={tab === 'products' ? ivpStyles.tabActive : ivpStyles.tabInactive}>Top items</button>
        </div>
      </div>

      {tab === 'groups' && (
        <div>
          {groupBreakdown.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: '13px', padding: '16px 0' }}>No group data yet</div>
          ) : (
            groupBreakdown.map(({ group, value }) => {
              const pct = Math.round((value / totalValue) * 100);
              const barW = Math.max(2, Math.round((value / maxGroupValue) * 100));
              return (
                <div key={group} style={ivpStyles.groupRow}>
                  <div style={ivpStyles.groupLabelRow}>
                    <span style={ivpStyles.groupName}>{group}</span>
                    <span style={ivpStyles.groupValue}>₹{value.toLocaleString('en-IN')}</span>
                    <span style={ivpStyles.groupPct}>{pct}%</span>
                  </div>
                  <div style={ivpStyles.barTrack}>
                    <div style={{ ...ivpStyles.barFill, width: `${barW}%` }} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === 'products' && (
        <div>
          {topValueProducts.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: '13px', padding: '16px 0' }}>No priced products yet</div>
          ) : (
            topValueProducts.map((p, i) => {
              const barW = Math.max(2, Math.round((p.totalValue / maxProductValue) * 100));
              return (
                <div key={p.id} style={ivpStyles.productRow}>
                  <div style={ivpStyles.productRankBadge}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={ivpStyles.productLabelRow}>
                      <span style={ivpStyles.productName}>{p.name}</span>
                      <span style={ivpStyles.productValue}>₹{p.totalValue.toLocaleString('en-IN')}</span>
                    </div>
                    <div style={ivpStyles.productMeta}>
                      {p.stock} units × ₹{p.costPrice.toLocaleString('en-IN')}
                      <span style={ivpStyles.groupTag}>{p.group}</span>
                    </div>
                    <div style={ivpStyles.barTrack}>
                      <div style={{ ...ivpStyles.barFillProduct, width: `${barW}%` }} />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const ivpStyles = {
  panel: { background: '#ffffff', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6', borderRadius: '14px', padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  emptyBox: { background: '#ffffff', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6', borderRadius: '14px', padding: '32px 22px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' },
  subtitle: { fontSize: '13px', display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px' },
  totalValueBig: { fontSize: '22px', fontWeight: '800', color: '#8b5cf6', letterSpacing: '-0.02em' },
  pricedCount: { fontSize: '12px', color: '#94a3b8' },
  tabs: { display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: '8px', padding: '3px' },
  tabActive: { padding: '5px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#ffffff', color: '#0f172a', fontSize: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  tabInactive: { padding: '5px 14px', borderRadius: '6px', border: 'none', backgroundColor: 'transparent', color: '#64748b', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  groupRow: { marginBottom: '12px' },
  groupLabelRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' },
  groupName: { fontSize: '13px', fontWeight: '600', color: '#1e293b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  groupValue: { fontSize: '13px', fontWeight: '700', color: '#8b5cf6', flexShrink: 0 },
  groupPct: { fontSize: '11px', color: '#94a3b8', minWidth: '32px', textAlign: 'right', flexShrink: 0 },
  barTrack: { height: '6px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' },
  barFill: { height: '100%', background: 'linear-gradient(to right, #c4b5fd, #8b5cf6)', borderRadius: '99px', transition: 'width 0.4s ease' },
  barFillProduct: { height: '100%', background: 'linear-gradient(to right, #a5b4fc, #6366f1)', borderRadius: '99px', transition: 'width 0.4s ease' },
  productRow: { display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'flex-start' },
  productRankBadge: { width: '22px', height: '22px', borderRadius: '50%', background: '#f5f3ff', color: '#8b5cf6', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
  productLabelRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' },
  productName: { fontSize: '13px', fontWeight: '600', color: '#1e293b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  productValue: { fontSize: '13px', fontWeight: '700', color: '#6366f1', flexShrink: 0 },
  productMeta: { fontSize: '11px', color: '#94a3b8', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '6px' },
  groupTag: { background: '#eff6ff', color: '#3b82f6', padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' },
};

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { orgId, currentUser, userRole } = useAuth();
  const [products, setProducts]   = useState([]);
  const [todayLogs, setTodayLogs] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [isMobile, setIsMobile]   = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Live products
  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, 'products'), where('orgId', '==', orgId));
    return onSnapshot(q, snap => {
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [orgId]);

  // Today's activity (live)
  useEffect(() => {
    if (!orgId) return;
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const q = query(
      collection(db, 'activityLog'),
      where('orgId', '==', orgId),
      where('timestamp', '>=', Timestamp.fromDate(startOfDay)),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    return onSnapshot(q, snap => setTodayLogs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [orgId]);

  // Recent 10 logs for activity feed
  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, 'activityLog'),
      where('orgId', '==', orgId),
      orderBy('timestamp', 'desc'),
      limit(10)
    );
    return onSnapshot(q, snap => setRecentLogs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [orgId]);

  // 7-day sales chart
  useEffect(() => {
    if (!orgId) return;
    const fetchChart = async () => {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        days.push({ date: d, label: d.toLocaleDateString('en-IN', { weekday: 'short' }) });
      }
      const start = new Date(days[0].date); start.setHours(0, 0, 0, 0);
      const q = query(
        collection(db, 'activityLog'),
        where('orgId', '==', orgId),
        where('timestamp', '>=', Timestamp.fromDate(start)),
        where('transactionType', '==', 'sale'),
        orderBy('timestamp', 'asc')
      );
      const snap = await getDocs(q);
      const counts = {};
      snap.docs.forEach(d => {
        const date = d.data().timestamp?.toDate();
        if (!date) return;
        const key = date.toLocaleDateString('en-IN', { weekday: 'short' });
        counts[key] = (counts[key] || 0) + Math.abs(d.data().delta || 0);
      });
      setChartData(days.map(d => ({ label: d.label, sales: counts[d.label] || 0 })));
    };
    fetchChart();
  }, [orgId]);

  const isLow = p => p.minStock != null ? p.stock < p.minStock : p.stock < p.moq;
  const lowStockProducts = products.filter(isLow).sort((a, b) => a.stock - b.stock);
  const productsWithCost = products.filter(p => p.costPrice != null && p.costPrice > 0);
  const totalValue = products.reduce((sum, p) => sum + (p.stock * (p.costPrice || 0)), 0);
  const todaySales = todayLogs.filter(l => l.transactionType === 'sale').reduce((s, l) => s + Math.abs(l.delta || 0), 0);
  const todayTxns = todayLogs.filter(l => !['created', 'delete'].includes(l.transactionType)).length;
  const outOfStock = products.filter(p => p.stock === 0).length;

  const valueByGroup = {};
  productsWithCost.forEach(p => {
    const val = p.stock * p.costPrice;
    valueByGroup[p.group] = (valueByGroup[p.group] || 0) + val;
  });
  const groupBreakdown = Object.entries(valueByGroup)
    .map(([group, value]) => ({ group, value }))
    .sort((a, b) => b.value - a.value);

  const topValueProducts = productsWithCost
    .map(p => ({ ...p, totalValue: p.stock * p.costPrice }))
    .sort((a, b) => b.totalValue - a.totalValue)
    .slice(0, 5);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'there';

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📦</div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '32px' }}>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: '800', color: '#0f172a', marginBottom: '4px', letterSpacing: '-0.02em' }}>
          {greeting}, {displayName} 👋
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b' }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          {lowStockProducts.length > 0 && (
            <Link to="/stock" style={{ textDecoration: 'none' }}>
              <span style={{ marginLeft: '10px', background: '#fef2f2', color: '#dc2626', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', border: '1px solid #fecaca', cursor: 'pointer' }}>
                🚨 {lowStockProducts.length} low stock
              </span>
            </Link>
          )}
        </p>
      </div>

      {/* Stat cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '28px',
      }}>
        <StatCard icon="📦" label="Total Products" value={products.length} sub="in inventory" color="#3b82f6" bg="#eff6ff" to="/stock" />
        <StatCard icon="🚨" label="Low Stock" value={lowStockProducts.length} sub={outOfStock > 0 ? `${outOfStock} out of stock` : 'needs attention'} color="#ef4444" bg="#fef2f2" to="/stock" />
        <StatCard icon="📈" label="Units Sold Today" value={todaySales} sub={`${todayTxns} transactions`} color="#16a34a" bg="#f0fdf4" to="/log" />
      </div>

      {/* ← NEW: Demand Forecast panel */}
      {products.length > 0 && (
        <DemandForecastPanel
          products={products}
          lowStockProducts={lowStockProducts}
          orgId={orgId}
          isMobile={isMobile}
        />
      )}

      {/* Inventory Value expanded panel */}
      <div style={{ marginBottom: '28px' }}>
        <InventoryValuePanel
          totalValue={totalValue}
          groupBreakdown={groupBreakdown}
          topValueProducts={topValueProducts}
          productsWithCost={productsWithCost}
          totalProducts={products.length}
          isMobile={isMobile}
        />
      </div>

      {/* Sales activity label */}
      <div style={{ marginBottom: '8px' }}>
        <h2 style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
          📈 Sales Activity
        </h2>
      </div>

      {/* Main grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '20px',
        marginBottom: '20px',
      }}>
        {/* Sales chart */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>Sales — Last 7 Days</h3>
              <p style={{ fontSize: '12px', color: '#94a3b8' }}>Units sold per day</p>
            </div>
            <Link to="/analytics" style={{ fontSize: '12px', color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>Full analytics →</Link>
          </div>
          {chartData.every(d => d.sales === 0) ? (
            <div style={{ height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '13px', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '28px' }}>📊</span>
              No sales recorded this week
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                  formatter={(v) => [`${v} units`, 'Sales']}
                />
                {chartData.map((entry, i) => (
                  <Bar key={i} dataKey="sales" radius={[4, 4, 0, 0]}>
                    {chartData.map((e, idx) => (
                      <Cell key={idx} fill={e.label === chartData[chartData.length - 1].label ? '#3b82f6' : '#bfdbfe'} />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Low stock panel */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>Low Stock Alerts</h3>
              <p style={{ fontSize: '12px', color: '#94a3b8' }}>Items needing restock</p>
            </div>
            <Link to="/stock" style={{ fontSize: '12px', color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>Manage →</Link>
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {lowStockProducts.length === 0 ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                <span style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }}>✅</span>
                All stock levels look good!
              </div>
            ) : (
              lowStockProducts.slice(0, 8).map(p => <LowStockRow key={p.id} product={p} />)
            )}
          </div>
          {lowStockProducts.length > 8 && (
            <Link to="/stock" style={{ fontSize: '12px', color: '#3b82f6', display: 'block', textAlign: 'center', marginTop: '8px', textDecoration: 'none', fontWeight: '600' }}>
              +{lowStockProducts.length - 8} more →
            </Link>
          )}
        </div>
      </div>

      {/* Today's activity feed */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>Recent Activity</h3>
            <p style={{ fontSize: '12px', color: '#94a3b8' }}>Latest transactions across your inventory</p>
          </div>
          <Link to="/log" style={{ fontSize: '12px', color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>Full log →</Link>
        </div>
        {recentLogs.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
            <span style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }}>🕓</span>
            No activity yet — start logging transactions
          </div>
        ) : (
          <div>{recentLogs.map(log => <MiniActivityRow key={log.id} log={log} />)}</div>
        )}
      </div>
    </div>
  );
}